import { Box } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { useMemo } from 'react'
import { ProductChangeover } from 'Features/Projects/Scenarios/EditSettings/LineSettings/LineSettings.types'
import { BaseTable } from 'Features/Projects/Table/BaseTable.view'
import {
  useUpdateProductChangeovers,
  useUploadProductChangeovers,
} from './LineSettings.data'
import { toast } from 'react-hot-toast'
import { useSettingsContext } from '../EditSettings.types'

interface ProductChangeOversTableProps {
  data: ProductChangeover[]
  isLoading?: boolean
}

const onSuccess = () => {
  toast.success('Update Succesful')
}

const onError = () => {
  toast.error('Update Failed')
}

export const ProductChangeOversTable = ({
  data,
  isLoading,
}: ProductChangeOversTableProps) => {
  const COLUMN_WIDTH = 150

  const { state } = useSettingsContext()
  const { projectId, scenarioId } = state
  const { mutate, isLoading: isSaving } = useUpdateProductChangeovers(
    onSuccess,
    onError
  )
  const onUploadSuccess = () => {
    toast.success('Upload Successful')
  }
  const onUploadError = () => {
    toast.error('Upload Failed')
  }
  const { mutate: uploadMutate, isPending: isUploading } =
    useUploadProductChangeovers(onUploadSuccess, onUploadError)

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Base Source Site Code',
        width: COLUMN_WIDTH,
        flex: 1,
        type: 'string',
        pinned: true,
      },
      {
        field: 'workcentreCode',
        headerName: 'Work Centre Code',
        width: COLUMN_WIDTH,
        flex: 1,
        type: 'string',
        pinned: true,
      },
      {
        field: 'fromSkuGroupCode',
        headerName: 'From SKU Group Code',
        width: COLUMN_WIDTH,
        type: 'string',
        pinned: true,
      },
      {
        field: 'toSkuGroupCode',
        headerName: 'To SKU Group Code',
        width: COLUMN_WIDTH,
        type: 'string',
        pinned: true,
      },
      {
        field: 'fromProductFormat',
        headerName: 'From Product Format',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'toProductFormat',
        headerName: 'To Product Format',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'fromBatchGroupingCode',
        headerName: 'From Batch Group Code',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'toBatchGroupingCode',
        headerName: 'To Batch Group Code',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'changeOverTimeInMinutes',
        headerName: 'Change Over Time (Minutes)',
        width: COLUMN_WIDTH,
        type: 'number',
        editable: true,
        valueFormatter: (value: number) => {
          if (typeof value === 'string') {
            return Number(value)
          }
          return value === null ? '' : value.toFixed(2)
        },
      },
      {
        field: 'changeOverCostInCurrency',
        headerName: 'Change Over Cost (Currency)',
        width: COLUMN_WIDTH,
        type: 'number',
        valueFormatter: (value: number) => {
          if (typeof value === 'string') {
            return Number(value)
          }
          return value === null ? '' : value.toFixed(2)
        },
      },
      {
        field: 'changeOverCostInCarbon',
        headerName: 'Change Over Cost (Carbon)',
        width: COLUMN_WIDTH,
        type: 'number',
        valueFormatter: (value: number) => {
          if (typeof value === 'string') {
            return Number(value)
          }
          return value === null ? '' : value.toFixed(2)
        },
      },
      // Hidden fields
      // Commented out hidden fields
      // {
      //   field: 'baseSourceSiteKey',
      //   headerName: 'Base Source Site Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'fromSKUGroupKey',
      //   headerName: 'From SKU Group Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'toSKUGroupKey',
      //   headerName: 'To SKU Group Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'id',
      //   headerName: 'ID',
      //   width: COLUMN_WIDTH,
      //   type: 'string',
      //   hide: true,
      // },
      // {
      //   field: 'workcentreKey',
      //   headerName: 'Work Centre Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
    ]
  }, [])

  const updateTableData = (changedRows: Record<string, ProductChangeover>) => {
    mutate({
      changedRows,
      projectId,
      scenarioId,
    })
  }

  const uploadData = (file: File) => {
    uploadMutate({ file, projectId, scenarioId })
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        overflow: 'auto',
        flexDirection: 'column',
      }}
    >
      <BaseTable
        data={data}
        columns={columns}
        isLoading={isLoading ?? false}
        updateTableData={updateTableData}
        uploadData={uploadData}
        isUploading={isUploading ?? false}
        isSaving={isSaving}
        checkBoxSelection={true}
        pinnedColumns={[
          'baseSourceSiteCode',
          'workcentreCode',
          'fromSkuGroupCode',
          'toSkuGroupCode',
        ]}
        customDownloadDataMethod={(tableData) => tableData}
      />
    </Box>
  )
}
